import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faCircleCheck,
  faClock,
  faTriangleExclamation,
  faCalendarDays,
  faChalkboardUser,
  faUserGraduate,
  faEnvelope,
  faRotate,
  faCheck,
  faWandMagicSparkles,
  faArrowRight,
  faCircleInfo,
  faForwardFast,
  faCalendarXmark,
} from '@fortawesome/free-solid-svg-icons';
import './Studentschedulelegacy.css'; // LEGACY: old global classes other old pages may use
import './StudentSchedule.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

/* Same conversions as before (date-fns, browser time) */
const formatForDateTimeInput = (dateString) => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};
const parse = (s) => {
  if (!s) return null;
  try {
    const d = parseISO(s);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};
const fmtDay = (d) => format(d, 'EEE, MMM d');
const fmtYearDay = (d) => format(d, 'EEE, MMM d, yyyy');
const fmtTime = (d) => format(d, 'h:mm a');
const sameYear = (d) => d.getFullYear() === new Date().getFullYear();

/* "Every Monday · 4:00 PM" preview for a datetime-local value */
const weeklyLabel = (value) => {
  const d = parse(value);
  return d ? `Every ${format(d, 'EEEE')} · ${fmtTime(d)} · starting ${format(d, 'MMM d')}` : '';
};

const EMPTY_EDIT = {
  ClassDateTime: '',
  IdTeacher: '',
  TeacherFullName: '',
  done: false,
  TeacherJoin: '',
  StudentJoin: '',
  Issue: false,
  sendEmailCancelTeacher: false,
  sendEmailRescheduleTeacher: false,
  sendEmailCancelParent: false,
  sendEmailRescheduleParent: false,
};
const EMPTY_ADD = { nbSessions: 8, sendEmailNew: false, sendEmailUpdate: false, teacherId: '' };
const todayValue = () => format(new Date(), 'yyyy-MM-dd');
const CANCEL_KEYS = ['sendEmailCancelTeacher', 'sendEmailCancelParent'];
const MISSED_DAYS = 60; // missed classes older than this stay in "past"
const joined = (s) => !!(s.TeacherJoin || s.StudentJoin);

const EMAILS = [
  ['sendEmailRescheduleParent', 'Reschedule email to parent'],
  ['sendEmailCancelParent', 'Cancel email to parent'],
  ['sendEmailRescheduleTeacher', 'Reschedule email to teacher'],
  ['sendEmailCancelTeacher', 'Cancel email to teacher'],
];

/* Same props as before: { studentId, studentType } — studentType is true for demo students */
const StudentSchedule = ({ studentId, studentType, assignedTeacher }) => {
  const [schedules, setSchedules] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);
  const [showPast, setShowPast] = useState(false);

  const [editing, setEditing] = useState(null); // schedule row
  const [formData, setFormData] = useState(EMPTY_EDIT);
  const [submitting, setSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addFormData, setAddFormData] = useState(EMPTY_ADD);
  const [slots, setSlots] = useState([{ id: 1, dateTime: '' }]);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [suggest, setSuggest] = useState({ loading: false, list: [], after: null, error: '' }); // from the weekly slots

  const [deleting, setDeleting] = useState(null); // schedule row
  const [bulkOpen, setBulkOpen] = useState(false); // delete all upcoming classes
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  // cancelled class -> moved to the next free slot after the last class
  const [moveInfo, setMoveInfo] = useState(null); // { state: 'loading'|'moved'|'error', label, error, original }

  // "Update from slots": move all upcoming classes onto the weekly slots
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [slotsStart, setSlotsStart] = useState(todayValue());
  const [slotsPreview, setSlotsPreview] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [slotsApplying, setSlotsApplying] = useState(false);
  const [slotsEmails, setSlotsEmails] = useState({ sendEmailTeacher: false, sendEmailParent: false });

  // ---------- Load (same endpoints) ----------
  const fetchData = useCallback(async () => {
    const [scheduleRes, teacherRes] = await Promise.all([
      axios.get(`${API}/api/admin/studentprofile/schedule/${studentId}`, auth()),
      axios.get(`${API}/api/admin/studentprofile/teachers/list`, auth()),
    ]);
    setSchedules(Array.isArray(scheduleRes.data) ? scheduleRes.data : []);
    setTeachers(Array.isArray(teacherRes.data) ? teacherRes.data : []);
  }, [studentId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchData()
      .catch((err) => {
        if (!cancelled) setLoadError(err.response?.data?.error || 'We couldn’t load the schedule.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchData, reload]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Escape closes the open window
  useEffect(() => {
    if (!editing && !addOpen && !deleting && !slotsOpen && !bulkOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (!submitting) setEditing(null);
      if (!adding) setAddOpen(false);
      if (!deleteBusy) setDeleting(null);
      if (!slotsApplying) setSlotsOpen(false);
      if (!bulkBusy) setBulkOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, addOpen, deleting, slotsOpen, bulkOpen, submitting, adding, deleteBusy, slotsApplying, bulkBusy]);

  // ---------- Update from slots ----------
  const loadSlotsPreview = useCallback(
    async (startDate) => {
      setSlotsLoading(true);
      setSlotsError('');
      try {
        const res = await axios.post(`${API}/api/admin/studentprofile/applyslots/${studentId}`, { apply: false, startDate }, auth());
        setSlotsPreview(res.data);
      } catch (err) {
        setSlotsPreview(null);
        setSlotsError(err.response?.data?.error || 'Couldn’t prepare the new schedule.');
      } finally {
        setSlotsLoading(false);
      }
    },
    [studentId]
  );

  const openSlots = () => {
    const start = todayValue();
    setSlotsStart(start);
    setSlotsEmails({ sendEmailTeacher: false, sendEmailParent: false });
    setSlotsPreview(null);
    setSlotsOpen(true);
    loadSlotsPreview(start);
  };

  const onSlotsStart = (e) => {
    const v = e.target.value || todayValue();
    setSlotsStart(v);
    loadSlotsPreview(v);
  };

  const applySlots = async () => {
    setSlotsApplying(true);
    setSlotsError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/studentprofile/applyslots/${studentId}`,
        { apply: true, startDate: slotsStart, ...slotsEmails },
        auth()
      );
      await fetchData();
      setSlotsOpen(false);
      const n = res.data?.applied || 0;
      const failed = res.data?.emailErrors?.length ? ' Some emails could not be sent.' : '';
      setToast(n ? `${n} ${n === 1 ? 'class' : 'classes'} moved to the new schedule.${failed}` : 'Already on the weekly slots — nothing to change.');
    } catch (err) {
      setSlotsError(err.response?.data?.error || 'Couldn’t update the classes.');
    } finally {
      setSlotsApplying(false);
    }
  };

  // ---------- Add (same endpoints + body) ----------
  const openAdd = () => {
    // the student's assigned teacher is picked by default (can still be changed)
    const assigned = assignedTeacher && teachers.some((t) => String(t.id) === String(assignedTeacher)) ? String(assignedTeacher) : '';
    setAddFormData({ ...EMPTY_ADD, teacherId: assigned });
    setSlots([{ id: 1, dateTime: '' }]);
    setAddError('');
    setAddOpen(true);
    if (studentType) return;
    // suggestions only: the admin decides which ones to use
    setSuggest({ loading: true, list: [], after: null, error: '' });
    axios
      .get(`${API}/api/admin/studentprofile/slotsuggestions/${studentId}`, auth())
      .then((res) => setSuggest({ loading: false, list: res.data?.suggestions || [], after: res.data?.after || null, error: '' }))
      .catch(() => setSuggest({ loading: false, list: [], after: null, error: 'Couldn’t load the weekly slots.' }));
  };

  // put a suggested time in the first empty box, or in a new one
  const pickSuggestion = (value) =>
    setSlots((list) => {
      if (list.some((x) => x.dateTime === value)) return list;
      const empty = list.find((x) => !x.dateTime);
      if (empty) return list.map((x) => (x === empty ? { ...x, dateTime: value } : x));
      return [...list, { id: Math.max(0, ...list.map((x) => x.id)) + 1, dateTime: value }];
    });
  const pickAllSuggestions = () => {
    const values = suggest.list.map((x) => x.ClassDateTime);
    setSlots((list) => {
      const kept = list.filter((x) => x.dateTime && !values.includes(x.dateTime));
      let id = Math.max(0, ...list.map((x) => x.id));
      return [...kept, ...values.map((v) => ({ id: (id += 1), dateTime: v }))];
    });
  };
  const addSlot = () => setSlots((s) => [...s, { id: Math.max(0, ...s.map((x) => x.id)) + 1, dateTime: '' }]);
  const removeSlot = (id) => setSlots((s) => (s.length <= 1 ? s : s.filter((x) => x.id !== id)));
  const updateSlot = (id, value) => setSlots((s) => s.map((x) => (x.id === id ? { ...x, dateTime: value } : x)));
  const onAddField = (e) => {
    const { name, value, type, checked } = e.target;
    setAddFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setAddError('');
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const validSlots = slots.filter((s) => s.dateTime !== '').map((s) => s.dateTime);
    if (validSlots.length === 0) {
      setAddError('Please select at least one class time');
      return;
    }
    setAdding(true);
    setAddError('');
    try {
      await axios.post(
        `${API}/api/admin/studentprofile/${studentType ? 'addscheduledemo' : 'addschedule'}/${studentId}`,
        {
          classDateTimes: validSlots,
          nbSessions: parseInt(addFormData.nbSessions, 10),
          sendEmailNew: addFormData.sendEmailNew,
          sendEmailUpdate: addFormData.sendEmailUpdate,
          teacherId: addFormData.teacherId,
        },
        auth()
      );
      setAddOpen(false);
      await fetchData();
      setToast(studentType ? 'Demo class added.' : 'Schedule created.');
    } catch (err) {
      setAddError(err.response?.data?.error || err.message || 'Couldn’t create the schedule.');
    } finally {
      setAdding(false);
    }
  };

  // ---------- Edit (same endpoint + body) ----------
  const openEdit = (s, { moveToEnd = false } = {}) => {
    setEditing(s);
    setEditError('');
    setMoveInfo(null);
    if (moveToEnd) moveClassToEnd(s);
    setFormData({
      ClassDateTime: formatForDateTimeInput(s.ClassDateTime),
      IdTeacher: s.IdTeacher ?? '',
      TeacherFullName: s.TeacherFullName ?? '',
      done: s.done === 1,
      TeacherJoin: formatForDateTimeInput(s.TeacherJoin),
      StudentJoin: formatForDateTimeInput(s.StudentJoin),
      Issue: s.Issue === 1,
      sendEmailCancelTeacher: false,
      sendEmailRescheduleTeacher: false,
      sendEmailCancelParent: false,
      sendEmailRescheduleParent: false,
    });
  };

  const onEditField = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setEditError('');
    if (name === 'ClassDateTime' && moveInfo?.state === 'moved') setMoveInfo((m) => ({ ...m, state: 'manual' }));
    if (CANCEL_KEYS.includes(name)) onCancelToggle(name, checked);
  };

  // Cancel email ticked -> move the class to the next free slot after the last class.
  // Unticked (both) -> back to the original time, unless the date was changed by hand.
  const onCancelToggle = async (name, checked) => {
    const otherOn = CANCEL_KEYS.filter((k) => k !== name).some((k) => formData[k]);
    const original = formatForDateTimeInput(editing?.ClassDateTime);
    if (!checked) {
      if (!otherOn && moveInfo && moveInfo.by === 'cancel') {
        if (moveInfo.state === 'moved') setFormData((f) => ({ ...f, ClassDateTime: moveInfo.original }));
        setMoveInfo(null);
      }
      return;
    }
    if (otherOn || moveInfo) return; // already moved for the other box
    if (formData.ClassDateTime !== original) return; // date already changed by hand: keep it
    moveClassToEnd(editing, 'cancel');
  };

  // Put the class on the next free weekly slot after the student's last class
  const moveClassToEnd = async (row, by = 'button') => {
    const original = formatForDateTimeInput(row.ClassDateTime);
    setMoveInfo({ state: 'loading', original, by });
    try {
      const res = await axios.get(`${API}/api/admin/studentprofile/nextslot/${studentId}?exclude=${row.Id}`, auth());
      setFormData((f) => ({ ...f, ClassDateTime: res.data.ClassDateTime }));
      setMoveInfo({ state: 'moved', original, by, label: res.data.label, lastClass: res.data.lastClass });
    } catch (err) {
      setMoveInfo({ state: 'error', original, by, error: err.response?.data?.error || 'Couldn’t find the next free slot.' });
    }
  };

  // ---------- Delete all upcoming classes ----------
  const confirmBulkDelete = async () => {
    setBulkBusy(true);
    setBulkError('');
    try {
      const res = await axios.delete(`${API}/api/admin/studentprofile/deleteupcoming/${studentId}`, auth());
      await fetchData();
      setBulkOpen(false);
      const n = res.data?.deleted || 0;
      setToast(n ? `${n} upcoming ${n === 1 ? 'class' : 'classes'} deleted.` : 'No upcoming classes to delete.');
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Couldn’t delete the classes.');
    } finally {
      setBulkBusy(false);
    }
  };

  const keepOriginalTime = () => {
    setFormData((f) => ({ ...f, ClassDateTime: moveInfo.original }));
    setMoveInfo((m) => ({ ...m, state: 'kept' }));
  };

  const onTeacher = (e) => {
    const id = e.target.value;
    // ids can be numbers from the server and strings from the <select>, so compare as text
    const t = teachers.find((x) => String(x.id) === String(id));
    setFormData((f) => ({ ...f, IdTeacher: id, TeacherFullName: t ? t.name : '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setEditError('');
    try {
      await axios.put(
        `${API}/api/admin/studentprofile/updateschedule/${editing.Id}/${studentId}`,
        { ...formData, done: formData.done ? 1 : 0, Issue: formData.Issue ? 1 : 0 },
        auth()
      );
      await fetchData();
      setEditing(null);
      setToast('Class updated.');
    } catch (err) {
      setEditError(err.response?.data?.error || err.message || 'Couldn’t save this class.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Delete (same endpoint) ----------
  const confirmDelete = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/studentprofile/deleteschedule/${deleting.Id}/${studentId}`, auth());
      await fetchData();
      setDeleting(null);
      setToast('Class deleted.');
    } catch (err) {
      setDeleteError(err.response?.data?.error || err.message || 'Couldn’t delete this class.');
    } finally {
      setDeleteBusy(false);
    }
  };

  /* ---------- Derived ---------- */
  const now = new Date();
  const rows = schedules
    .map((s, i) => ({ ...s, _d: parse(s.ClassDateTime), _i: i }))
    .sort((a, b) => (a._d?.getTime() || 0) - (b._d?.getTime() || 0))
    .map((s, i) => ({ ...s, _n: i + 1 })); // session number in date order
  const upcoming = rows.filter((s) => s.done !== 1 && s._d && s._d >= now);
  // missed = in the past, not completed, the student never joined (recent ones only): needs a new date
  const missedFrom = new Date(now.getTime() - MISSED_DAYS * 86400000);
  const missedRows = rows.filter((s) => s.done !== 1 && s._d && s._d < now && s._d >= missedFrom && !s.StudentJoin);
  const past = rows.filter((s) => !(s.done !== 1 && s._d && s._d >= now) && !missedRows.includes(s)).reverse(); // newest first
  const deletable = upcoming.filter((s) => !joined(s));
  const next = upcoming[0];
  const stats = {
    total: rows.length,
    done: rows.filter((s) => s.done === 1).length,
    upcoming: upcoming.length,
    missed: rows.filter((s) => s.done !== 1 && s._d && s._d < now).length,
    issues: rows.filter((s) => s.Issue === 1).length,
  };
  const teacherName = (s) => s.TeacherFullName || teachers.find((t) => String(t.id) === String(s.IdTeacher))?.name || '';

  if (loading) {
    return (
      <div className="rmass" aria-busy="true">
        <div className="rmass-skel rmass-skel--bar" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rmass-skel" />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rmass">
        <div className="rmass-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{loadError}</p>
          <button type="button" className="rmass-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const renderRow = (s) => {
    const missed = s.done !== 1 && s._d && s._d < now;
    const tj = parse(s.TeacherJoin);
    const sj = parse(s.StudentJoin);
    const tone = s.done === 1 ? 'is-good' : missed ? 'is-bad' : 'is-info';
    return (
      <li key={s.Id ?? s._i} className={`rmass-row ${s === next ? 'is-next' : ''}`}>
        <span className={`rmass-num ${tone}`}>{s._n}</span>
        <div className="rmass-when">
          <strong>{s._d ? (sameYear(s._d) ? fmtDay(s._d) : fmtYearDay(s._d)) : 'No date'}</strong>
          <span>{s._d ? fmtTime(s._d) : ''}</span>
        </div>
        <div className="rmass-who">
          {teacherName(s) ? (
            <span className="rmass-cap">
              <FontAwesomeIcon icon={faChalkboardUser} /> {teacherName(s)}
            </span>
          ) : (
            <span className="rmass-dim">No teacher</span>
          )}
          <span className="rmass-joins">
            <span className={tj ? 'is-on' : ''} title="Teacher joined">
              <FontAwesomeIcon icon={tj ? faCheck : faXmark} /> TJ{tj ? ` ${fmtTime(tj)}` : ''}
            </span>
            <span className={sj ? 'is-on' : ''} title="Student joined">
              <FontAwesomeIcon icon={sj ? faCheck : faXmark} /> SJ{sj ? ` ${fmtTime(sj)}` : ''}
            </span>
          </span>
        </div>
        <div className="rmass-tags">
          <span className={`rmass-pill ${tone}`}>
            <FontAwesomeIcon icon={s.done === 1 ? faCircleCheck : missed ? faTriangleExclamation : faClock} />
            {s.done === 1 ? 'Completed' : missedRows.includes(s) ? 'Missed' : missed ? 'Not completed' : s === next ? 'Next class' : 'Upcoming'}
          </span>
          {s.Issue === 1 && (
            <span className="rmass-pill is-bad">
              <FontAwesomeIcon icon={faTriangleExclamation} /> Issue
            </span>
          )}
        </div>
        <div className="rmass-actions">
          {missedRows.includes(s) && (
            <button type="button" className="rmass-moveend" onClick={() => openEdit(s, { moveToEnd: true })} title="Move to the next free slot after the last class">
              <FontAwesomeIcon icon={faForwardFast} /> <span>Move to end</span>
            </button>
          )}
          <button type="button" onClick={() => openEdit(s)} aria-label="Edit class">
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button type="button" className="is-danger" onClick={() => { setDeleteError(''); setDeleting(s); }} aria-label="Delete class">
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="rmass">
      {/* ---------- Summary + add ---------- */}
      <div className="rmass-head">
        <ul className="rmass-stats">
          <li>
            <b>{stats.total}</b> classes
          </li>
          <li className="is-good">
            <b>{stats.done}</b> completed
          </li>
          <li className="is-info">
            <b>{stats.upcoming}</b> upcoming
          </li>
          {stats.missed > 0 && (
            <li className="is-bad">
              <b>{stats.missed}</b> not completed
            </li>
          )}
          {stats.issues > 0 && (
            <li className="is-bad">
              <b>{stats.issues}</b> with issue
            </li>
          )}
        </ul>
        <div className="rmass-head-actions">
          <button type="button" className="rmass-icon" onClick={() => setReload((n) => n + 1)} aria-label="Reload schedule" title="Reload">
            <FontAwesomeIcon icon={faRotate} />
          </button>
          {!studentType && deletable.length > 0 && (
            <button type="button" className="rmass-btn rmass-btn--ghost rmass-btn--warn" onClick={() => { setBulkError(''); setBulkOpen(true); }} title="Delete all upcoming classes">
              <FontAwesomeIcon icon={faCalendarXmark} /> Delete upcoming
            </button>
          )}
          {!studentType && upcoming.length > 0 && (
            <button type="button" className="rmass-btn rmass-btn--ghost" onClick={openSlots} title="Move all upcoming classes onto the student's weekly slots">
              <FontAwesomeIcon icon={faWandMagicSparkles} /> Update from slots
            </button>
          )}
          <button type="button" className="rmass-btn" onClick={openAdd}>
            <FontAwesomeIcon icon={faPlus} /> {studentType ? 'Add demo class' : 'Add schedule'}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rmass-empty">
          <FontAwesomeIcon icon={faCalendarDays} />
          <p>No classes scheduled yet.</p>
          <button type="button" className="rmass-btn" onClick={openAdd}>
            <FontAwesomeIcon icon={faPlus} /> {studentType ? 'Add demo class' : 'Create the schedule'}
          </button>
        </div>
      ) : (
        <>
          {missedRows.length > 0 && (
            <>
              <h3 className="rmass-title is-bad">
                <FontAwesomeIcon icon={faTriangleExclamation} /> Missed — give {missedRows.length === 1 ? 'it' : 'them'} a new date
              </h3>
              <ul className="rmass-list rmass-list--missed">{missedRows.map(renderRow)}</ul>
            </>
          )}

          <h3 className="rmass-title">Upcoming</h3>
          {upcoming.length ? (
            <ul className="rmass-list">
              {upcoming.map(renderRow)}
            </ul>
          ) : (
            <p className="rmass-none">No upcoming classes.</p>
          )}

          {past.length > 0 && (
            <>
              <button type="button" className="rmass-toggle" onClick={() => setShowPast((v) => !v)} aria-expanded={showPast}>
                {showPast ? 'Hide' : 'Show'} past classes <span>{past.length}</span>
              </button>
              {showPast && (
                <ul className="rmass-list">
                  {past.map(renderRow)}
                </ul>
              )}
            </>
          )}
        </>
      )}

      {/* ---------- Edit class ---------- */}
      {editing && (
        <div className="rmass-overlay" onClick={() => !submitting && setEditing(null)}>
          <form className="rmass-modal" role="dialog" aria-modal="true" aria-label="Edit class" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <header className="rmass-modal-head">
              <h2>Edit class {editing._n ? `#${editing._n}` : ''}</h2>
              <button type="button" className="rmass-x" onClick={() => setEditing(null)} disabled={submitting} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmass-modal-body">
              <div className="rmass-two">
                <label className="rmass-field">
                  <span>Teacher</span>
                  <select name="IdTeacher" value={formData.IdTeacher} onChange={onTeacher} required disabled={submitting}>
                    <option value="">Select a teacher</option>
                    {teachers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="rmass-field">
                  <span>Class date & time</span>
                  <input type="datetime-local" name="ClassDateTime" value={formData.ClassDateTime} onChange={onEditField} required disabled={submitting} />
                </label>
              </div>

              {!moveInfo && editing.done !== 1 && (
                <button type="button" className="rmass-linkbtn" onClick={() => moveClassToEnd(editing)} disabled={submitting}>
                  <FontAwesomeIcon icon={faForwardFast} /> Put it at the end of the schedule
                </button>
              )}

              {moveInfo && moveInfo.state !== 'kept' && (
                <div className={`rmass-note ${moveInfo.state === 'error' ? 'is-bad' : 'is-info'}`} role="status">
                  <FontAwesomeIcon icon={moveInfo.state === 'error' ? faTriangleExclamation : faCircleInfo} />
                  {moveInfo.state === 'loading' && <span>Finding the next free slot…</span>}
                  {moveInfo.state === 'error' && <span>{moveInfo.error} Set the new date by hand.</span>}
                  {moveInfo.state === 'moved' && (
                    <span>
                      Moved to the end of the schedule: <b>{moveInfo.label}</b>
                      {moveInfo.lastClass ? ` (after the last class, ${moveInfo.lastClass})` : ''}. For a reschedule, just change the date above.
                      <button type="button" onClick={keepOriginalTime} disabled={submitting}>
                        Keep the original time
                      </button>
                    </span>
                  )}
                  {moveInfo.state === 'manual' && <span>Date changed by hand — it will be saved as you set it.</span>}
                </div>
              )}

              <div className="rmass-two">
                {[
                  ['TeacherJoin', 'Teacher joined at', faChalkboardUser],
                  ['StudentJoin', 'Student joined at', faUserGraduate],
                ].map(([name, label, icon]) => (
                  <label key={name} className="rmass-field">
                    <span>
                      <FontAwesomeIcon icon={icon} /> {label}
                    </span>
                    <div className="rmass-clear">
                      <input type="datetime-local" name={name} value={formData[name]} onChange={onEditField} disabled={submitting} />
                      {formData[name] && (
                        <button type="button" onClick={() => setFormData((f) => ({ ...f, [name]: '' }))} aria-label={`Clear ${label}`} title="Clear">
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              <div className="rmass-switches">
                <label className={`rmass-switch is-good ${formData.done ? 'is-on' : ''}`}>
                  <input type="checkbox" name="done" checked={formData.done} onChange={onEditField} disabled={submitting} />
                  <i aria-hidden="true" />
                  <span>Class completed</span>
                </label>
                <label className={`rmass-switch is-bad ${formData.Issue ? 'is-on' : ''}`}>
                  <input type="checkbox" name="Issue" checked={formData.Issue} onChange={onEditField} disabled={submitting} />
                  <i aria-hidden="true" />
                  <span>Had an issue</span>
                </label>
              </div>

              <fieldset className="rmass-emails">
                <legend>
                  <FontAwesomeIcon icon={faEnvelope} /> Emails to send when you save
                </legend>
                {EMAILS.map(([name, label]) => (
                  <label key={name} className="rmass-check">
                    <input type="checkbox" name={name} checked={formData[name]} onChange={onEditField} disabled={submitting} />
                    <span>{label}</span>
                  </label>
                ))}
              </fieldset>

              {editError && <p className="rmass-error">{editError}</p>}
            </div>
            <footer className="rmass-modal-foot">
              <button type="button" className="rmass-btn rmass-btn--ghost" onClick={() => setEditing(null)} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="rmass-btn" disabled={submitting || moveInfo?.state === 'loading'}>
                {submitting ? 'Saving…' : 'Save changes'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {/* ---------- Add schedule ---------- */}
      {addOpen && (
        <div className="rmass-overlay" onClick={() => !adding && setAddOpen(false)}>
          <form className="rmass-modal" role="dialog" aria-modal="true" aria-label="Add schedule" onClick={(e) => e.stopPropagation()} onSubmit={handleAddSubmit}>
            <header className="rmass-modal-head">
              <h2>{studentType ? 'Add demo class' : 'New paid schedule'}</h2>
              <button type="button" className="rmass-x" onClick={() => setAddOpen(false)} disabled={adding} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmass-modal-body">
              <div className={studentType ? '' : 'rmass-two'}>
                <label className="rmass-field">
                  <span>Teacher</span>
                  <select name="teacherId" value={addFormData.teacherId} onChange={onAddField} required disabled={adding}>
                    <option value="">Select a teacher</option>
                    {teachers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                {!studentType && (
                  <label className="rmass-field">
                    <span>Number of sessions (total)</span>
                    <input type="number" name="nbSessions" value={addFormData.nbSessions} onChange={onAddField} min="1" max="52" required disabled={adding} />
                  </label>
                )}
              </div>

              {studentType ? (
                <label className="rmass-field">
                  <span>Class date & time</span>
                  <input type="datetime-local" value={slots[0]?.dateTime || ''} onChange={(e) => updateSlot(slots[0].id, e.target.value)} required disabled={adding} />
                </label>
              ) : (
                <div className="rmass-field">
                  <span>Weekly class times</span>
                  <div className="rmass-suggest">
                    <div className="rmass-suggest-head">
                      <strong>
                        <FontAwesomeIcon icon={faWandMagicSparkles} /> From the weekly slots
                      </strong>
                      {suggest.list.length > 1 && (
                        <button type="button" onClick={pickAllSuggestions} disabled={adding}>
                          Use all
                        </button>
                      )}
                    </div>
                    {suggest.loading ? (
                      <small>Loading…</small>
                    ) : suggest.error ? (
                      <small>{suggest.error}</small>
                    ) : suggest.list.length === 0 ? (
                      <small>No weekly slots picked. Choose them in the Slots tab to get suggestions.</small>
                    ) : (
                      <>
                        <small>{suggest.after ? `Next dates after the last class (${suggest.after}). Tap to use.` : 'Next dates from today. Tap to use.'}</small>
                        <ul>
                          {suggest.list.map((x) => {
                            const used = slots.some((sl) => sl.dateTime === x.ClassDateTime);
                            return (
                              <li key={x.ClassDateTime}>
                                <button type="button" className={used ? 'is-on' : ''} onClick={() => pickSuggestion(x.ClassDateTime)} disabled={adding || used} aria-pressed={used}>
                                  <FontAwesomeIcon icon={used ? faCheck : faPlus} />
                                  <span>
                                    <b>{x.slot}</b>
                                    <em>from {x.label}</em>
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </div>
                  <ul className="rmass-slots">
                    {slots.map((slot, i) => (
                      <li key={slot.id}>
                        <input type="datetime-local" value={slot.dateTime} onChange={(e) => updateSlot(slot.id, e.target.value)} required={i === 0} disabled={adding} aria-label={`Class time ${i + 1}`} />
                        {slots.length > 1 && (
                          <button type="button" onClick={() => removeSlot(slot.id)} aria-label="Remove this time" disabled={adding}>
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        )}
                        {slot.dateTime && <small>{weeklyLabel(slot.dateTime)}</small>}
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="rmass-addslot" onClick={addSlot} disabled={adding}>
                    <FontAwesomeIcon icon={faPlus} /> Add another day / time
                  </button>
                  <small>Add every day and time the student has class each week. The first date of each is when it starts.</small>
                </div>
              )}

              {!studentType && (
                <fieldset className="rmass-emails">
                  <legend>
                    <FontAwesomeIcon icon={faEnvelope} /> Emails
                  </legend>
                  <label className="rmass-check">
                    <input type="checkbox" name="sendEmailNew" checked={addFormData.sendEmailNew} onChange={onAddField} disabled={adding} />
                    <span>Send email for the new schedule</span>
                  </label>
                  <label className="rmass-check">
                    <input type="checkbox" name="sendEmailUpdate" checked={addFormData.sendEmailUpdate} onChange={onAddField} disabled={adding} />
                    <span>Send email for schedule updates</span>
                  </label>
                </fieldset>
              )}

              {addError && <p className="rmass-error">{addError}</p>}
            </div>
            <footer className="rmass-modal-foot">
              <button type="button" className="rmass-btn rmass-btn--ghost" onClick={() => setAddOpen(false)} disabled={adding}>
                Cancel
              </button>
              <button type="submit" className="rmass-btn" disabled={adding}>
                {adding ? 'Adding…' : studentType ? 'Add demo class' : 'Create schedule'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {/* ---------- Update from slots ---------- */}
      {slotsOpen && (
        <div className="rmass-overlay" onClick={() => !slotsApplying && setSlotsOpen(false)}>
          <div className="rmass-modal" role="dialog" aria-modal="true" aria-label="Update from slots" onClick={(e) => e.stopPropagation()}>
            <header className="rmass-modal-head">
              <h2>Update upcoming classes</h2>
              <button type="button" className="rmass-x" onClick={() => setSlotsOpen(false)} disabled={slotsApplying} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmass-modal-body">
              <p className="rmass-lead">
                Moves every upcoming class onto the student’s weekly slots. Same number of classes and same teacher, only the dates change. The
                teacher gets one notification.
              </p>

              <div className="rmass-two">
                <label className="rmass-field">
                  <span>New schedule starts on</span>
                  <input type="date" value={slotsStart} min={todayValue()} onChange={onSlotsStart} disabled={slotsApplying} />
                  <small>Classes before this day stay as they are.</small>
                </label>
                <div className="rmass-field">
                  <span>Weekly slots</span>
                  <p className="rmass-slotline">{slotsPreview?.slots || (slotsLoading ? '…' : '—')}</p>
                  <small>Change them in the Slots tab.</small>
                </div>
              </div>

              {slotsLoading && !slotsPreview ? (
                <div className="rmass-skel" />
              ) : (
                slotsPreview && (
                  <>
                    <p className="rmass-movecount">
                      <b>{slotsPreview.changed}</b> of {slotsPreview.count} upcoming {slotsPreview.count === 1 ? 'class' : 'classes'} will move
                    </p>
                    <ol className={`rmass-moves ${slotsLoading ? 'is-busy' : ''}`}>
                      {slotsPreview.changes.map((c) => (
                        <li key={c.id} className={c.same ? 'is-same' : ''}>
                          <span className="rmass-move-from">{c.from}</span>
                          <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                          <span className="rmass-move-to">{c.same ? 'No change' : c.to}</span>
                        </li>
                      ))}
                    </ol>
                  </>
                )
              )}

              {slotsPreview?.changed > 0 && (
                <fieldset className="rmass-emails">
                  <legend>
                    <FontAwesomeIcon icon={faEnvelope} /> Emails with the new dates
                  </legend>
                  {[
                    ['sendEmailTeacher', 'Email to teacher'],
                    ['sendEmailParent', 'Email to parent'],
                  ].map(([name, label]) => (
                    <label key={name} className="rmass-check">
                      <input
                        type="checkbox"
                        checked={slotsEmails[name]}
                        onChange={(e) => setSlotsEmails((m) => ({ ...m, [name]: e.target.checked }))}
                        disabled={slotsApplying}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </fieldset>
              )}

              {slotsError && <p className="rmass-error">{slotsError}</p>}
            </div>
            <footer className="rmass-modal-foot">
              <button type="button" className="rmass-btn rmass-btn--ghost" onClick={() => setSlotsOpen(false)} disabled={slotsApplying}>
                Cancel
              </button>
              <button type="button" className="rmass-btn" onClick={applySlots} disabled={slotsApplying || slotsLoading || !slotsPreview?.changed}>
                {slotsApplying
                  ? 'Updating…'
                  : slotsPreview?.changed
                  ? `Move ${slotsPreview.changed} ${slotsPreview.changed === 1 ? 'class' : 'classes'}`
                  : 'Nothing to change'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ---------- Delete all upcoming ---------- */}
      {bulkOpen && (
        <div className="rmass-overlay" onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div className="rmass-dialog" role="alertdialog" aria-modal="true" aria-label="Delete upcoming classes" onClick={(e) => e.stopPropagation()}>
            <span className="rmass-dialog-icon">
              <FontAwesomeIcon icon={faCalendarXmark} />
            </span>
            <h2>
              Delete {deletable.length} upcoming {deletable.length === 1 ? 'class' : 'classes'}?
            </h2>
            <p>
              {deletable.length
                ? `${fmtDay(deletable[0]._d)} → ${fmtYearDay(deletable[deletable.length - 1]._d)}. Completed and past classes are kept. The teacher is not notified.`
                : 'Nothing to delete.'}
            </p>
            {bulkError && <p className="rmass-error">{bulkError}</p>}
            <div className="rmass-dialog-actions">
              <button type="button" className="rmass-btn rmass-btn--ghost" onClick={() => setBulkOpen(false)} disabled={bulkBusy} autoFocus>
                Cancel
              </button>
              <button type="button" className="rmass-btn rmass-btn--danger" onClick={confirmBulkDelete} disabled={bulkBusy || !deletable.length}>
                {bulkBusy ? 'Deleting…' : `Delete ${deletable.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Delete ---------- */}
      {deleting && (
        <div className="rmass-overlay" onClick={() => !deleteBusy && setDeleting(null)}>
          <div className="rmass-dialog" role="alertdialog" aria-modal="true" aria-label="Delete class" onClick={(e) => e.stopPropagation()}>
            <span className="rmass-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2>Delete this class?</h2>
            <p>
              {deleting._d ? `${fmtYearDay(deleting._d)} · ${fmtTime(deleting._d)}` : 'This class'}
              {teacherName(deleting) ? ` with ${teacherName(deleting)}` : ''}
            </p>
            {deleteError && <p className="rmass-error">{deleteError}</p>}
            <div className="rmass-dialog-actions">
              <button type="button" className="rmass-btn rmass-btn--ghost" onClick={() => setDeleting(null)} disabled={deleteBusy} autoFocus>
                Cancel
              </button>
              <button type="button" className="rmass-btn rmass-btn--danger" onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmass-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default StudentSchedule;